// ============================================================
// EsignSendPanel.tsx — B4c batch 1b: the send window's third way, "Send for
// e-signature" (pilot only). Loads the server's checklist
// (GET …/statements/:id/esign) and shows it in the new ActionBox:
//   • each missing item is a row with a button that fixes it — approve the
//     consent wording / open E-signature settings (admins), add a mobile or
//     email (Client details / the litigation friend), record email consent
//     (switches to "By email", where the consent form is);
//   • when everything is ready: who it goes to (their email + masked mobile)
//     and what happens next; the window's Send button sends it
//     (POST …/esign/send — the parent owns the button + the send key);
//   • already sent and still open → says so (resend / cancel are on the
//     statement card).
// (E-sign audit fixes A) The SECOND STAFF CHECK: the signer's mobile / email
// changed in the last 14 days → each change is listed plainly, and a
// colleague (never whoever made the change; audit batch 2: they may then send it) must confirm the details before Send
// works — Ask a colleague to confirm → (another user) Confirm the details are
// right → Confirmed by … A firm with nobody else to confirm sends without it
// (the completion certificate says so). With no recent change, the last
// changes are a small muted line. The parent makes the calls.
// (E-sign fixes B)
//   • who CAN confirm is named ("Ask a colleague to confirm: Priya Shah or Tom
//     Reed can."); nobody → why, and what an admin can change, with the other
//     colleagues and why each can't (needs "See colleagues' billing", no
//     access to this case). The single-user note only when nobody else at the
//     firm is an active user;
//   • exactly ONE thing missing → the slim one-line notice, not the checklist;
//   • the pilot off → says an admin can switch it on.
// (D12) The visible ask → answer flow: "Ask a colleague to confirm" (hidden
// when nobody can) notifies every eligible colleague; the sender then sees
// "Waiting for Jackson Collins to confirm…", then "Confirmed by … on …" or
// "… didn't confirm: <reason>" + Ask again. The colleague sees each change
// (what, old → new masked, who, when) and can Confirm or Don't confirm (a
// reason is required — RefuseCheckDialog, the newer modal over this window).
// ============================================================
import React, { useId, useState } from 'react'
import ActionBox, { ErrorNote } from '../../shared/ActionBox'
import ModalFrame from '../../shared/ModalFrame'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import { B_PRIMARY, B_SECONDARY, BIcon, INPUT, NOTE } from '../../billing/billingUi'
import { ukDateTime } from '../../esign/esignFormat'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'
import { SECOND_CHECK_REFUSE_MAX, type EsignContactChange, type EsignContactCheck, type EsignFixTarget, type StatementEsign } from '../../../types/esign.types'
import { ESIGN_STATUS_LABEL, isOpenEsign } from './EsignStatus'

export const ESIGN_CHECKLIST_TITLE = 'Before you can send it for e-signature'

// "30 Oct 2026" (UK time).
export const shortUkDate = (iso: string | null | undefined): string => {
  const d = iso ? new Date(iso) : null
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' }) : ''
}
// "Mobile changed on 30 Oct 2026 by Priya Shah: 07•••••111 → 07•••••123"
export const contactChangeLine = (what: 'Mobile' | 'Email', c: EsignContactChange): string =>
  `${what} changed on ${shortUkDate(c.at)}${c.byName ? ` by ${c.byName}` : ''}: ${c.from || 'none'} → ${c.to || 'none'}`
const lastChanged = (what: 'Mobile' | 'Email', c: EsignContactChange | null): string | null =>
  c ? `${what} last changed ${shortUkDate(c.at)}${c.byName ? ` by ${c.byName}` : ''}` : null

export const SECOND_CHECK_NEEDED = (days: number): string =>
  `The mobile number or email changed in the last ${days} days, so a colleague who didn’t make the change must confirm the details before the link can be sent. Whoever confirms can send it.`
export const SINGLE_USER_NOTE = 'You’re the only active user at your firm, so nobody else can confirm. It will be sent without a second check — the completion certificate will say so.'
// (E-sign fixes B) "Priya Shah", "Priya Shah or Tom Reed", "A, B or C".
export const orList = (names: string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
export const CONFIRMERS_LINE = (names: string[]): string => `Ask a colleague to confirm: ${orList(names)} can.`
export const NOBODY_CAN_CONFIRM = 'Nobody else can confirm yet. Ask an admin to give a colleague “See colleagues’ billing” (Settings → Permissions) and access to this case.'
export const ESIGN_OFF_MESSAGE = 'An admin can turn it on in Settings → E-signature.'

// Who can confirm (from the server); nothing when an older server didn't say.
const WhoCanConfirm: React.FC<{ check: EsignContactCheck }> = ({ check }) => {
  if (!check.confirmers) return null
  if (check.confirmers.length) return <p className="m-0 mt-[6px] text-[13px] leading-[1.45] text-casevo-forest" data-confirmers="">{CONFIRMERS_LINE(check.confirmers)}</p>
  const others = check.others ?? []
  return (
    <div className="mt-[6px]" data-confirmers="none">
      <p className="m-0 text-[13px] leading-[1.45] text-casevo-forest">{NOBODY_CAN_CONFIRM}</p>
      {others.length > 0 && (
        <ul className="m-0 mt-[4px] pl-[18px] list-disc text-[12.5px] leading-[1.5] text-casevo-text-soft" aria-label="Colleagues who can’t confirm yet" data-confirm-others="">
          {others.map((o, i) => <li key={`${o.name}-${i}`}><span className="text-casevo-forest">{o.name}</span> — {o.reason}</li>)}
        </ul>
      )}
    </div>
  )
}

// (D12) "Waiting for Jackson Collins to confirm…" — who was asked (else who can).
export const waitingTitle = (names: string[]): string =>
  names.length ? `Waiting for ${orList(names)} to confirm…` : 'Waiting for a colleague to confirm…'
export const confirmedTitle = (name: string | null, at: string | null): string =>
  `Confirmed by ${name ?? 'a colleague'}${at ? ` on ${ukDateTime(at)}` : ''}`
export const refusedTitle = (name: string | null | undefined, reason: string | null | undefined): string =>
  `${name ?? 'A colleague'} didn’t confirm${reason ? `: ${reason}` : ''}`
export const REFUSE_REASON_REQUIRED = 'Say why you aren’t confirming the details.'

// Each change, for the colleague confirming: what, old → new (masked), who, when.
const ChangeTable: React.FC<{ check: EsignContactCheck }> = ({ check }) => (
  <ul className="m-0 mt-[8px] p-0 list-none flex flex-col gap-[6px]" data-contact-changes="" aria-label="What changed">
    {check.recent.map((c, i) => (
      <li key={`${c.channel}-${c.at}-${i}`} className="rounded-[8px] border border-casevo-border-soft bg-casevo-surface px-[10px] py-[6px] text-[13px] leading-[1.45]" data-change={c.channel}>
        <span className="font-semibold text-casevo-forest">{c.channel === 'MOBILE' ? 'Mobile' : 'Email'}</span>
        <span className="block tabular-nums text-casevo-forest break-words"><span className="sr-only">from </span>{c.from || 'none'} <span aria-hidden="true">→</span><span className="sr-only"> to </span> <b>{c.to || 'none'}</b></span>
        <span className="block text-[12.5px] text-casevo-text-soft">Changed {ukDateTime(c.at)}{c.byName ? ` by ${c.byName}` : ''}</span>
      </li>
    ))}
  </ul>
)

// (D12) The colleague doesn't confirm — the reason is required (the newer modal, over the Send window).
export const RefuseCheckDialog: React.FC<{ busy: boolean; error: string | null; onClose: () => void; onRefuse: (reason: string) => Promise<boolean> }> = ({ busy, error, onClose, onRefuse }) => {
  const id = useId()
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)
  const guard = useUnsavedChangesGuard(reason.trim() !== '' && !busy)
  const close = (): void => { if (!busy) guard(onClose) }
  const err = !reason.trim() ? REFUSE_REASON_REQUIRED : null
  const submit = async (): Promise<void> => {
    setTried(true)
    if (err || busy) return
    if (await onRefuse(reason.trim())) onClose()
  }
  return (
    <ModalFrame size="sm" portal onClose={close} dismissible={!busy} eyebrow="E-signature" eyebrowIcon={<BIcon name="alert" size={12} />}
      title="Don’t confirm the details?" subtitle="The link isn’t sent. The person who asked sees your reason and can ask again once it’s sorted."
      footerRight={<><button type="button" className={B_SECONDARY} disabled={busy} onClick={close}>Cancel</button>
        <button type="button" className={B_PRIMARY} data-refuse-submit="" disabled={busy} aria-busy={busy || undefined} onClick={() => { void submit() }}>{busy ? 'Saving…' : 'Don’t confirm'}</button></>}>
      <FormField label="Why aren’t you confirming them?" required variant="info" htmlFor={`${id}-reason`}>
        <textarea id={`${id}-reason`} className={`${INPUT} h-auto min-h-[96px] py-2`} value={reason} maxLength={SECOND_CHECK_REFUSE_MAX} required disabled={busy}
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={(tried && !!err) || undefined} aria-describedby={`${id}-count${tried && err ? ` ${id}-err` : ''}`} />
        <span id={`${id}-err`}>{tried && <FieldError message={err ?? undefined} />}</span>
        <span id={`${id}-count`} className={`${NOTE} block mt-[4px]`}>{reason.length} / {SECOND_CHECK_REFUSE_MAX}</span>
      </FormField>
      {error && <div className="mt-[10px]"><ErrorNote>{error}</ErrorNote></div>}
    </ModalFrame>
  )
}

export type CheckBusy = 'ask' | 'approve' | 'refuse' | null
export const ContactCheckBox: React.FC<{
  check: EsignContactCheck
  busy: CheckBusy
  error: string | null
  onAsk: () => void
  onApprove: (approvalId: string) => void
  // (D12) Returns true once recorded (the dialog closes).
  onRefuse?: (approvalId: string, reason: string) => Promise<boolean>
}> = ({ check, busy, error, onAsk, onApprove, onRefuse }) => {
  const [refusing, setRefusing] = useState(false)
  if (!check.recent.length) {
    const facts = [lastChanged('Mobile', check.mobile), lastChanged('Email', check.email)].filter(Boolean)
    return facts.length ? <p className={`${NOTE} m-0`} data-contact-facts="">{facts.join(' · ')}.</p> : null
  }
  const changes = (
    <ul className="m-0 mt-[6px] pl-[18px] list-disc text-[13px] leading-[1.5] text-casevo-forest" data-contact-changes="">
      {check.recent.map((c, i) => <li key={`${c.channel}-${c.at}-${i}`}>{contactChangeLine(c.channel === 'MOBILE' ? 'Mobile' : 'Email', c)}</li>)}
    </ul>
  )
  const a = check.approval
  // (D12) Nobody can confirm → no "Ask" button (WhoCanConfirm says why). An older server (no list) → offered.
  const canAsk = !check.confirmers || check.confirmers.length > 0
  const askAction = (label: string) => (canAsk ? [{ label, kind: 'primary' as const, onClick: onAsk, busy: busy === 'ask', busyLabel: 'Asking…', disabled: !!busy }] : [])
  let box: React.ReactNode
  if (a?.status === 'APPROVED') {
    box = <ActionBox tone="done" live title={confirmedTitle(a.approvedBy, a.approvedAt)} message="The contact details were checked with the client. You can send it.">{changes}</ActionBox>
  } else if (check.singleUserFirm) {
    box = <ActionBox tone="info" title="The contact details changed recently" message={SINGLE_USER_NOTE}>{changes}</ActionBox>
  } else if (a?.status === 'PENDING' && check.canApprove) {
    box = (
      <ActionBox tone="warn" live title={`${a.requestedBy || 'A colleague'} asked you to confirm the client’s contact details`}
        message={<>Before confirming, check with the client — on a number you already have — that these details are theirs. Asked {ukDateTime(a.createdAt)}.</>}
        actions={[
          ...(onRefuse ? [{ label: 'Don’t confirm…', kind: 'link' as const, onClick: () => setRefusing(true), disabled: !!busy }] : []),
          { label: 'Confirm the details are right', kind: 'primary', onClick: () => onApprove(a.id), busy: busy === 'approve', busyLabel: 'Confirming…', disabled: !!busy },
        ]}>
        <ChangeTable check={check} />
      </ActionBox>
    )
  } else if (a?.status === 'PENDING') {
    const asked = a.askedNames?.length ? a.askedNames : (check.confirmers ?? [])
    box = (
      <ActionBox tone="info" live title={waitingTitle(asked)}
        message={a.askedNames?.length
          ? 'We’ve sent them a notification and an email. They open this statement and confirm the details — this updates by itself.'
          : check.confirmers ? 'They open this statement here and confirm the details.' : 'Ask someone else who can see this statement to open it here and confirm.'}>
        {changes}{!a.askedNames?.length && <WhoCanConfirm check={check} />}
      </ActionBox>
    )
  } else if (a?.status === 'REFUSED') {
    box = (
      <ActionBox tone="danger" live title={refusedTitle(a.refusedBy, a.refuseReason)}
        message={<>{a.refusedAt ? `${ukDateTime(a.refusedAt)}. ` : ''}Check the details with the client and correct them if they’re wrong, then ask again.</>}
        actions={askAction('Ask again')}>
        {changes}<WhoCanConfirm check={check} />
      </ActionBox>
    )
  } else {
    box = (
      <ActionBox tone="warn" title="A colleague must confirm the contact details" message={SECOND_CHECK_NEEDED(check.windowDays)}
        actions={askAction('Ask a colleague to confirm')}>
        {changes}<WhoCanConfirm check={check} />
      </ActionBox>
    )
  }
  return (
    <div className="flex flex-col gap-[8px]" data-second-check={a?.status ?? (check.singleUserFirm ? 'SINGLE_USER' : check.needsSecondCheck ? 'NEEDED' : 'NONE')}>
      {box}
      {error && !refusing && <ErrorNote>{error}</ErrorNote>}
      {refusing && a && onRefuse && (
        <RefuseCheckDialog busy={busy === 'refuse'} error={error} onClose={() => setRefusing(false)} onRefuse={(reason) => onRefuse(a.id, reason)} />
      )}
    </div>
  )
}

const EsignSendPanel: React.FC<{
  esign: StatementEsign | null
  loadError: string | null
  onFix: (target: EsignFixTarget) => void
  onRetry: () => void
  // (audit fixes A) The second staff check (the parent makes the calls).
  checkBusy?: CheckBusy
  checkError?: string | null
  onAskSecondCheck?: () => void
  onApproveSecondCheck?: (approvalId: string) => void
  onRefuseSecondCheck?: (approvalId: string, reason: string) => Promise<boolean>
}> = ({ esign, loadError, onFix, onRetry, checkBusy = null, checkError = null, onAskSecondCheck = () => undefined, onApproveSecondCheck = () => undefined, onRefuseSecondCheck }) => {
  if (loadError) return <ErrorNote action={<button type="button" className="text-[13px] font-medium text-casevo-link hover:underline" onClick={onRetry}>Retry</button>}>{loadError}</ErrorNote>
  if (!esign) return <p className={`${NOTE} m-0`} role="status">Checking what’s needed…</p>
  if (!esign.available) return <div data-esign-off=""><ActionBox tone="info" title="E-signature isn’t switched on for your firm" message={ESIGN_OFF_MESSAGE} /></div>
  const open = isOpenEsign(esign.current) ? esign.current : null
  if (open) {
    return (
      <div data-esign-open="">
        <ActionBox tone="info" title={`Already sent for e-signature — ${ESIGN_STATUS_LABEL[open.status].toLowerCase()}`}
          message={`${open.signer?.name ?? 'The signer'} has a link. To send a new one, use Resend link on the statement; to stop it, use Cancel request.`} />
      </div>
    )
  }
  const items = esign.items.map((i) => ({
    key: i.key, done: i.done, message: i.message, hint: i.hint,
    actions: !i.done && i.fix ? [{ label: i.fix.label, kind: 'primary' as const, arrow: false, onClick: () => onFix(i.fix!.target) }] : undefined,
  }))
  // (E-sign fixes B) Exactly one thing missing → one line, not a checklist.
  const missing = esign.items.filter((i) => !i.done)
  const one = missing.length === 1 ? missing[0] : null
  const s = esign.signer
  const cc = esign.contactCheck ?? null
  const waiting = !!cc?.needsSecondCheck
  return (
    <div className="flex flex-col gap-[12px]" data-esign-panel="">
      {esign.sendableError && <ErrorNote>{esign.sendableError}</ErrorNote>}
      {!esign.ready && one && (
        // The line keeps ~14rem before the button wraps under it (no squeezed column at 360 px).
        <div data-esign-checklist="one" className="[&_p]:basis-[min(100%,14rem)]">
          <ActionBox variant="slim" tone="warn" ariaLabel={ESIGN_CHECKLIST_TITLE} title="Not ready yet:"
            message={<>{one.message}{/[.!?…”]$/.test(one.message) ? '' : '.'}{one.hint ? <> {one.hint}</> : null}</>}
            actions={one.fix ? [{ label: one.fix.label, kind: 'primary', onClick: () => onFix(one.fix!.target) }] : undefined} />
        </div>
      )}
      {!esign.ready && !one && <div data-esign-checklist=""><ActionBox tone="warn" title={ESIGN_CHECKLIST_TITLE} items={items} /></div>}
      {esign.ready && s && (
        <div data-esign-ready="">
          <ActionBox tone={waiting ? 'info' : 'done'} title={`${waiting ? 'Ready to send, once a colleague confirms the details, to' : 'Ready to send to'} ${s.name}${s.onBehalfOf ? ` (litigation friend for ${s.onBehalfOf})` : ''}`} items={items}>
            <ul className="m-0 mt-[8px] pl-[18px] text-[13px] leading-[1.5] text-casevo-forest list-disc">
              <li>We email a secure link to <b>{s.email}</b> from your Outlook (or Casevo’s mailbox if Outlook isn’t connected). The email names your firm and you — never any figures.</li>
              <li>When they open it, we text a 6-digit code to their mobile <b>{s.maskedMobile}</b>.</li>
              <li>They agree to sign online, read every page, tell you whether they agree with the statement, then sign.</li>
              <li>Sending counts as delivery by email. Their answer is recorded automatically, and the sealed PDF is emailed to them and to you.</li>
            </ul>
          </ActionBox>
        </div>
      )}
      {cc && <ContactCheckBox check={cc} busy={checkBusy} error={checkError} onAsk={onAskSecondCheck} onApprove={onApproveSecondCheck} onRefuse={onRefuseSecondCheck} />}
    </div>
  )
}

export default EsignSendPanel
